/**
 * This device's identity and its hybrid logical clock.
 *
 * Both have to survive a reload, for different reasons.
 *
 * `deviceId` is the final tie-breaker in the total order (sync protocol §2). A
 * device that invented a new one on every load would look like a new device each
 * time, and two events it wrote a second apart could order inconsistently
 * relative to another device's.
 *
 * The clock is worse: it only ever moves forward, so a fresh one would start
 * behind everything the device had already pushed. Edits made after a reload
 * would carry lower clocks than edits made before, lose the merge, and arrive as
 * `superseded` with no explanation a user could act on.
 *
 * Data model §6 puts both in the `meta` store of the Dexie replica. They live in
 * `localStorage` until that exists, which is a smaller promise: losing them costs
 * correctness of ordering, not data, and the server's log is unaffected.
 */
import {
  decode,
  encode,
  isImplausible,
  now,
  origin,
  receive,
  uuidv7,
  type Hlc,
} from '@intentionalist/shared';

const DEVICE_KEY = 'intentionalist.deviceId';
const CLOCK_KEY = 'intentionalist.hlc';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage denied. Ordering degrades; nothing is lost.
  }
}

/**
 * A stable id for this device.
 *
 * Must not contain whitespace, since it is embedded in the encoded clock. A
 * UUIDv7 satisfies that and needs no coordination to be unique.
 */
export function deviceId(): string {
  const existing = read(DEVICE_KEY);
  if (existing !== null && existing !== '' && !/\s/.test(existing)) return existing;
  const fresh = uuidv7();
  write(DEVICE_KEY, fresh);
  return fresh;
}

function current(): Hlc {
  const stored = read(CLOCK_KEY);
  if (stored !== null) {
    try {
      const decoded = decode(stored);
      // The stored clock may carry an older device id if storage was partly lost;
      // the local id is authoritative for anything this device issues.
      return { ...decoded, deviceId: deviceId() };
    } catch {
      // Unreadable. Starting from zero is safe: `now` takes the wall clock, which
      // is ahead of zero, so nothing regresses.
    }
  }
  return origin(deviceId());
}

const persist = (clock: Hlc): string => {
  const encoded = encode(clock);
  write(CLOCK_KEY, encoded);
  return encoded;
};

/** The clock for a new local write. Monotonic across reloads, which is the point. */
export const tick = (wall: number = Date.now()): string => persist(now(current(), wall));

/**
 * Folds an observed remote clock in, so the next local write exceeds it.
 *
 * Called for every pulled event. Without it, a device that had been offline would
 * keep issuing clocks below what the other device had already written, and lose
 * every merge until its wall clock caught up.
 */
export function observe(remoteHlc: string, wall: number = Date.now()): boolean {
  try {
    const remote = decode(remoteHlc);
    persist(receive(current(), remote, wall));
    // Reported rather than swallowed: a clock this far ahead means some device's
    // time is wrong, and only the person can fix that (ADR-0013). Phase 3 surfaces
    // it in the sync indicator FR-10.4 calls for.
    return isImplausible(remote, wall);
  } catch {
    // A malformed clock from the server is not something the client can fix, and
    // refusing to advance is the safe response.
    return false;
  }
}

/** Testing seam only. */
export function resetClock(): void {
  try {
    localStorage.removeItem(CLOCK_KEY);
    localStorage.removeItem(DEVICE_KEY);
  } catch {
    // Nothing depends on this having worked.
  }
}
